import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";

import { useAuth } from "../AuthContext";
import { t } from "../i18n";

/**
 * LifeIntroPopup — Fullscreen modal showing the Life illustration on the
 * home screen after login. Occupies the maximum available screen space
 * without distorting the image (`object-contain`). Dismissable via the
 * close button in the top-right corner; also closes on backdrop click and
 * on Escape. Remembers dismissal for the current login session via
 * sessionStorage so it does not re-appear on every navigation back to
 * the home screen.
 */
export default function LifeIntroPopup() {
  const { lang } = useAuth();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    try {
      const seen = sessionStorage.getItem("life_intro_seen") === "1";
      if (!seen) setOpen(true);
    } catch (_) { setOpen(true); }
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    // Lock underlying scroll while the popup is showing.
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") close(); };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const close = () => {
    try { sessionStorage.setItem("life_intro_seen", "1"); } catch (_) { /* ignore */ }
    setOpen(false);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="life-intro-popup"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
          className="fixed inset-0 z-[110] flex items-center justify-center bg-black/85 backdrop-blur-md p-2 sm:p-4"
          onClick={close}
        >
          <motion.div
            initial={{ scale: 0.94 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 180, damping: 20 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full h-full flex flex-col items-center justify-center gap-3 sm:gap-4"
          >
            <motion.h2
              data-testid="life-intro-heading"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
              className="shrink-0 px-4 text-center font-heading text-lg sm:text-2xl font-bold text-white tracking-tight leading-snug drop-shadow-[0_3px_10px_rgba(0,0,0,0.65)] max-w-[92%]"
            >
              {t(lang, "life_intro_heading")}
            </motion.h2>
            <img
              src="/life.png"
              alt="Life — Self, Body, Mind, Intellect and the five senses"
              data-testid="life-intro-image"
              draggable={false}
              className="min-h-0 max-w-full w-auto h-auto object-contain rounded-2xl shadow-[0_20px_60px_-10px_rgba(0,0,0,0.6)]"
              style={{ maxHeight: "calc(100% - 4.5rem)" }}
            />
            <button
              data-testid="life-intro-close-btn"
              onClick={close}
              aria-label="Close"
              className="absolute top-2 right-2 sm:top-3 sm:right-3 w-11 h-11 rounded-full bg-white/95 hover:bg-white text-slate-800 flex items-center justify-center border-2 border-white shadow-[0_4px_0_rgba(0,0,0,0.22)] active:translate-y-0.5 transition-all"
            >
              <X size={22} strokeWidth={3} />
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
